import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Search, AlertCircle, CheckCircle, Loader2, Database } from 'lucide-react';
import { apiFetch } from '@/utils/api';

export const Route = createFileRoute('/sales/debug-fruitsys')({
    component: DebugFruitsysPage,
})

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface QueryResult {
    success: boolean;
    query_info: {
        cost_center_id: string;
        cost_center_name: string;
        location_id: string;
        location_name: string;
        start_date: string;
        end_date: string;
        credentials_name: string;
    };
    summary: {
        total_orders: number;
        total_items: number;
        orders_without_items: number;
        orders_with_items: number;
        avg_items_per_order: number;
    };
    sample_orders: Array<{
        external_order_id: string;
        external_location_id: string;
        opened_at: string;
        closed_at: string;
        total_gross?: number;
        total_net?: number;
        discount_gross?: number;
        discount_net?: number;
        tip?: number;
        item_count: number;
        has_items: boolean;
        sample_items: any[];
    }>;
    recommendation: string;
}

function DebugFruitsysPage() {
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [selectedCostCenter, setSelectedCostCenter] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<QueryResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchCostCenters();

        // Set default dates (last 30 days)
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);

        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
    }, []);

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data.filter((cc: CostCenter) => cc.is_active));
            }
        } catch (error) {
            console.error('Error fetching cost centers:', error);
        }
    };

    const handleQuery = async () => {
        if (!selectedCostCenter || !startDate || !endDate) {
            setError('Please select a cost center and date range');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const params = new URLSearchParams({
                cost_center_id: selectedCostCenter,
                start_date: startDate,
                end_date: endDate,
            });

            const response = await apiFetch(`/api/fruit-bi-credentials/debug-query-fruitsys-orders?${params}`);

            if (response.ok) {
                const data = await response.json();
                setResult(data);
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to query Fruitsys');
            }
        } catch (error: any) {
            console.error('Error querying Fruitsys:', error);
            setError(error.message || 'Failed to query Fruitsys');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold">Debug Fruitsys Orders</h1>
                    <p className="text-muted-foreground mt-2">
                        Query order data directly from Fruitsys API to investigate sync issues
                    </p>
                </div>

                {/* Query Form */}
                <Card>
                    <CardHeader>
                        <CardTitle>Query Parameters</CardTitle>
                        <CardDescription>
                            Select a location and date range to query orders from Fruitsys
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="costCenter">Location (Cost Center)</Label>
                                <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                                    <SelectTrigger id="costCenter">
                                        <SelectValue placeholder="Select location..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {costCenters.map((cc) => (
                                            <SelectItem key={cc.id} value={cc.id}>
                                                {cc.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="startDate">Start Date</Label>
                                <Input
                                    id="startDate"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="endDate">End Date</Label>
                                <Input
                                    id="endDate"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <Button onClick={handleQuery} disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Querying Fruitsys...
                                </>
                            ) : (
                                <>
                                    <Search className="mr-2 h-4 w-4" />
                                    Query Fruitsys API
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>

                {/* Error Alert */}
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Results */}
                {result && (
                    <>
                        {/* Query Info */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Query Information</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <p className="text-muted-foreground">Location</p>
                                        <p className="font-medium">{result.query_info.cost_center_name}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Fruitsys Location</p>
                                        <p className="font-medium">{result.query_info.location_name} (ID: {result.query_info.location_id})</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Date Range</p>
                                        <p className="font-medium">{result.query_info.start_date} to {result.query_info.end_date}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Credentials</p>
                                        <p className="font-medium">{result.query_info.credentials_name}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Summary */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Summary</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                    <div className="text-center p-4 bg-muted rounded-lg">
                                        <p className="text-3xl font-bold">{result.summary.total_orders}</p>
                                        <p className="text-sm text-muted-foreground">Total Orders</p>
                                    </div>
                                    <div className="text-center p-4 bg-muted rounded-lg">
                                        <p className="text-3xl font-bold">{result.summary.total_items}</p>
                                        <p className="text-sm text-muted-foreground">Total Items</p>
                                    </div>
                                    <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                                        <p className="text-3xl font-bold text-green-600 dark:text-green-400">{result.summary.orders_with_items}</p>
                                        <p className="text-sm text-muted-foreground">With Items</p>
                                    </div>
                                    <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                                        <p className="text-3xl font-bold text-red-600 dark:text-red-400">{result.summary.orders_without_items}</p>
                                        <p className="text-sm text-muted-foreground">Without Items</p>
                                    </div>
                                    <div className="text-center p-4 bg-muted rounded-lg">
                                        <p className="text-3xl font-bold">{result.summary.avg_items_per_order.toFixed(1)}</p>
                                        <p className="text-sm text-muted-foreground">Avg Items/Order</p>
                                    </div>
                                </div>

                                <Alert className="mt-4">
                                    <Database className="h-4 w-4" />
                                    <AlertDescription>{result.recommendation}</AlertDescription>
                                </Alert>
                            </CardContent>
                        </Card>

                        {/* Sample Orders */}
                        {result.sample_orders.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Sample Orders (First 10)</CardTitle>
                                    <CardDescription>
                                        Preview of orders from Fruitsys API response
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Order ID</TableHead>
                                                    <TableHead>Opened</TableHead>
                                                    <TableHead>Closed</TableHead>
                                                    <TableHead className="text-right">Total Gross</TableHead>
                                                    <TableHead className="text-right">Total Net</TableHead>
                                                    <TableHead className="text-right">Discount</TableHead>
                                                    <TableHead className="text-right">Tip</TableHead>
                                                    <TableHead className="text-center">Items</TableHead>
                                                    <TableHead>Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {result.sample_orders.map((order, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell className="font-mono text-sm">{order.external_order_id}</TableCell>
                                                        <TableCell className="text-sm">{order.opened_at}</TableCell>
                                                        <TableCell className="text-sm">{order.closed_at}</TableCell>
                                                        <TableCell className="text-right">{order.total_gross?.toLocaleString()}</TableCell>
                                                        <TableCell className="text-right">{order.total_net?.toLocaleString()}</TableCell>
                                                        <TableCell className="text-right">{order.discount_gross?.toLocaleString()}</TableCell>
                                                        <TableCell className="text-right">{order.tip?.toLocaleString()}</TableCell>
                                                        <TableCell className="text-center">
                                                            <Badge variant={order.has_items ? "default" : "destructive"}>
                                                                {order.item_count}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            {order.has_items ? (
                                                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                                                    <CheckCircle className="mr-1 h-3 w-3" />
                                                                    Has Items
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                                                    <AlertCircle className="mr-1 h-3 w-3" />
                                                                    No Items
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default DebugFruitsysPage;
